import type { WarehouseTreeNode } from '@/lib/types';

/**
 * 倉庫ツリー（プレオーダー平坦リスト）の探索ユーティリティ（FKV3-12858）
 *
 * 店舗グループ別の出力では「画面のツリーで選んだグループ」が集計単位になる。
 * その選択から、出力範囲として送る末端倉庫IDを引く必要があるため、
 * ツリー選択コンポーネントとフォームの両方から使える形でここに置く。
 *
 * 入力は `node_type: 'seller_group'` の直後に配下が続くプレオーダー順で、
 * 配下は「level が自分より大きい連続範囲」で表現される。
 */

/** グループノードの index → 配下の末端倉庫IDリスト */
export function buildDescendantWarehouseIds(nodes: WarehouseTreeNode[]): Map<number, string[]> {
  const map = new Map<number, string[]>();
  nodes.forEach((node, index) => {
    if (node.node_type !== 'seller_group') return;
    const ids: string[] = [];
    for (let i = index + 1; i < nodes.length && nodes[i].level > node.level; i++) {
      if (nodes[i].node_type === 'warehouse') ids.push(nodes[i].id);
    }
    map.set(index, ids);
  });
  return map;
}

/**
 * 選んだグループの配下にある末端倉庫IDを集める。
 *
 * 同じグループIDが複数の index に現れても取りこぼさないよう、全 index を見る。
 */
export function expandGroupsToWarehouseIds(
  nodes: WarehouseTreeNode[],
  groupIds: Set<string>,
): Set<string> {
  const descendants = buildDescendantWarehouseIds(nodes);
  const warehouseIds = new Set<string>();
  nodes.forEach((node, index) => {
    if (node.node_type !== 'seller_group' || !groupIds.has(node.id)) return;
    (descendants.get(index) ?? []).forEach((id) => warehouseIds.add(id));
  });
  return warehouseIds;
}

/** 最上位（level が最小）のグループID。店舗グループ別の既定選択に使う */
export function topLevelGroupIds(nodes: WarehouseTreeNode[]): Set<string> {
  const groups = nodes.filter((n) => n.node_type === 'seller_group');
  if (groups.length === 0) return new Set();
  const minLevel = Math.min(...groups.map((n) => n.level));
  return new Set(groups.filter((n) => n.level === minLevel).map((n) => n.id));
}

/** あるグループの子孫にあたるグループIDを集める（自身は含まない） */
export function collectDescendantGroupIds(
  nodes: WarehouseTreeNode[],
  groupId: string,
): Set<string> {
  const descendants = new Set<string>();
  nodes.forEach((node, index) => {
    if (node.node_type !== 'seller_group' || node.id !== groupId) return;
    // level が自分より大きい連続範囲が配下
    for (let i = index + 1; i < nodes.length && nodes[i].level > node.level; i++) {
      if (nodes[i].node_type === 'seller_group') descendants.add(nodes[i].id);
    }
  });
  return descendants;
}

/**
 * あるグループの祖先にあたるグループIDを集める（自身は含まない）。
 *
 * 配下のチェックを外したときに「どの集計単位を解除するか」を引くのに使う。
 */
export function collectAncestorGroupIds(nodes: WarehouseTreeNode[], groupId: string): Set<string> {
  const ancestors = new Set<string>();
  nodes.forEach((node, index) => {
    if (node.node_type !== 'seller_group' || node.id !== groupId) return;
    // 手前へ遡り、level が単調に浅くなるノードを拾う
    let level = node.level;
    for (let i = index - 1; i >= 0; i--) {
      if (nodes[i].node_type !== 'seller_group' || nodes[i].level >= level) continue;
      ancestors.add(nodes[i].id);
      level = nodes[i].level;
      if (level === 1) break;
    }
  });
  return ancestors;
}
